import React, { useMemo } from 'react';
import { formatCurrency } from '../../../Help/helper';
import {
  computeExpenseBreakdown,
  computeReceivablesAging,
  computePayablesAging,
} from '../utils/reportAggregations';
import { EXPENSE_CATEGORIES } from '../constants';

const SectionHeader = ({ title }) => (
  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 rounded-t-xl">
    <h3 className="text-lg font-bold text-white">{title}</h3>
  </div>
);

const BreakdownTab = ({ data }) => {
  const { weekTransactions, allInvoices, openPayables } = data;

  const expenseBreakdown = useMemo(
    () => computeExpenseBreakdown(weekTransactions),
    [weekTransactions]
  );

  const receivablesAging = useMemo(
    () => computeReceivablesAging(allInvoices),
    [allInvoices]
  );

  const payablesAging = useMemo(
    () => computePayablesAging(openPayables),
    [openPayables]
  );

  // Collect all unique month keys for aging tables
  const recMonths = useMemo(() => {
    const months = new Set();
    Object.values(receivablesAging).forEach(m => Object.keys(m).forEach(k => months.add(k)));
    return [...months].sort().reverse();
  }, [receivablesAging]);

  const payMonths = useMemo(() => {
    const months = new Set();
    Object.values(payablesAging).forEach(m => Object.keys(m).forEach(k => months.add(k)));
    return [...months].sort().reverse();
  }, [payablesAging]);

  const totalWeekExpenses = EXPENSE_CATEGORIES.reduce(
    (sum, cat) => sum + (expenseBreakdown[cat] || []).reduce((s, item) => s + item.amount, 0),
    0
  );

  return (
    <div className="space-y-6">
      {/* Expense Breakdown */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Detailed Expense Breakdown" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {EXPENSE_CATEGORIES.map(cat => {
                const items = expenseBreakdown[cat] || [];
                if (items.length === 0) return null;
                const catTotal = items.reduce((s, item) => s + item.amount, 0);

                return (
                  <React.Fragment key={cat}>
                    {items.map((item, i) => (
                      <tr key={`${cat}-${i}`} className="hover:bg-gray-50">
                        {i === 0 && (
                          <td
                            rowSpan={items.length}
                            className="px-6 py-3 text-sm font-semibold text-gray-900 align-top border-r border-gray-100"
                          >
                            {cat}
                          </td>
                        )}
                        <td className="px-6 py-3 text-sm text-gray-900">{item.description}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{item.date}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">{item.modeOfPayment}</td>
                        <td className="px-6 py-3 text-sm text-right font-medium text-red-600">{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    {/* Category subtotal */}
                    <tr className="bg-gray-50">
                      <td colSpan={4} className="px-6 py-2 text-sm font-medium text-gray-700 text-right">{cat} Subtotal</td>
                      <td className="px-6 py-2 text-sm font-semibold text-right text-red-700">{formatCurrency(catTotal)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {/* Grand total */}
              <tr className="bg-gray-100 font-bold">
                <td colSpan={4} className="px-6 py-3 text-sm text-gray-900 text-right">Total Expenses</td>
                <td className="px-6 py-3 text-sm text-right text-red-800">{formatCurrency(totalWeekExpenses)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Receivables Aging */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Receivables Aging" />
        {Object.keys(receivablesAging).length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No outstanding receivables</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  {recMonths.map(m => (
                    <th key={m} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{m}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(receivablesAging)
                  .sort((a, b) => {
                    const totalA = Object.values(a[1]).reduce((s, v) => s + v, 0);
                    const totalB = Object.values(b[1]).reduce((s, v) => s + v, 0);
                    return totalB - totalA;
                  })
                  .map(([name, months]) => {
                    const total = Object.values(months).reduce((s, v) => s + v, 0);
                    return (
                      <tr key={name} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{name}</td>
                        {recMonths.map(m => (
                          <td key={m} className="px-4 py-3 text-sm text-right text-gray-700">
                            {months[m] ? formatCurrency(months[m]) : '-'}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-sm text-right font-semibold text-orange-600">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payables Aging */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Payables Aging" />
        {Object.keys(payablesAging).length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">No outstanding payables</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  {payMonths.map(m => (
                    <th key={m} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{m}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(payablesAging)
                  .sort((a, b) => {
                    const totalA = Object.values(a[1]).reduce((s, v) => s + v, 0);
                    const totalB = Object.values(b[1]).reduce((s, v) => s + v, 0);
                    return totalB - totalA;
                  })
                  .map(([supplier, months]) => {
                    const total = Object.values(months).reduce((s, v) => s + v, 0);
                    return (
                      <tr key={supplier} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{supplier}</td>
                        {payMonths.map(m => (
                          <td key={m} className="px-4 py-3 text-sm text-right text-gray-700">
                            {months[m] ? formatCurrency(months[m]) : '-'}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakdownTab;
